import { APP_ROUTES } from "@/constants/app-routes";
import { trackOnboardingV5StepSubmitted } from "@/features/onboarding/analytics";
import { OnboardingDarkBackground } from "@/features/onboarding/components/onboarding-dark-background";
import { OnboardingOptionPill } from "@/features/onboarding/components/onboarding-option-pill";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { OnboardingTopNav } from "@/features/onboarding/components/onboarding-top-nav";
import { OnboardingWarningAlert } from "@/features/onboarding/components/onboarding-warning-alert";
import { onboardingContentStyle } from "@/features/onboarding/constants/theme";
import { useGetMedical } from "@/hooks/requests/medical/use-get-medical";
import { useUpdateMedical } from "@/hooks/requests/medical/use-update-medical";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { triggerHaptic } from "@/utils/haptics";
import { safeAreaPadding } from "@/utils/safe-area-classes";
import { onboardingFadeTransition } from "@/utils/transitions/onboarding-fade-transition";
import { IonContent, IonPage, useIonRouter } from "@ionic/react";
import { useState } from "react";

const EATING_DISORDER_KEY = "eating-disorder";

type OptionKey = "under-18" | "eating-disorder" | "pregnant" | "medical-condition" | "none";

interface DiagnosticOption {
  key: OptionKey;
  label: string;
  sublabel?: string;
  /** Warning shown on select. Only "None of these apply" (no warning) advances. */
  warning?: string;
}

const OPTIONS: readonly DiagnosticOption[] = [
  {
    key: "under-18",
    label: "I'm under 18",
    warning: "We do not recommend using NutriCoach if you're under 18.",
  },
  {
    key: "eating-disorder",
    label: "I have an eating disorder",
    sublabel: "Or I'm currently in treatment",
    warning: "We do not recommend using NutriCoach with an active eating disorder.",
  },
  {
    key: "pregnant",
    label: "I'm pregnant or breastfeeding",
    warning: "We do not recommend using NutriCoach if you are pregnant or breastfeeding.",
  },
  {
    key: "medical-condition",
    label: "A medical condition affects my nutrition",
    warning:
      "We do not recommend using NutriCoach with a medical condition that affects my nutrition.",
  },
  {
    key: "none",
    label: "None of these apply",
  },
];

export default function DiagnosticPage() {
  const navigation = useIonRouter();
  const { medical, isLoading: isMedicalLoading } = useGetMedical();
  const { updateMedicalAsync, isUpdating } = useUpdateMedical();
  const { showError } = useToast();
  const [selected, setSelected] = useState<OptionKey | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const handleSelect = (option: DiagnosticOption) => {
    triggerHaptic();
    setSelected(option.key);
    // Every option except "None of these apply" blocks progress with an alert;
    // dismissing it leaves the user on this screen (Continue stays disabled).
    if (option.warning) setWarning(option.warning);
  };

  // Explicit rather than goBack(): this screen sits between two step-router screens,
  // so the back target is the name step regardless of how it was reached.
  const handleBack = () => {
    navigation.push(
      APP_ROUTES.ONBOARDING_FIRST_STEP,
      "back",
      "pop",
      undefined,
      onboardingFadeTransition,
    );
  };

  const handleContinue = async () => {
    // Only reachable with "None of these apply" selected.
    // PUT /medical replaces the whole object, so this has to be a read-modify-write.
    // Submitting before the GET lands would send empty arrays and wipe a real allergy.
    if (isUpdating || isMedicalLoading) return;
    triggerHaptic();
    try {
      await updateMedicalAsync({
        medicalConditions: (medical?.medicalConditions ?? []).filter(
          (c) => c !== EATING_DISORDER_KEY,
        ),
        allergies: medical?.allergies ?? [],
      });
      trackOnboardingV5StepSubmitted(APP_ROUTES.ONBOARDING_DIAGNOSTIC);
      navigation.push(
        APP_ROUTES.ONBOARDING_GOAL_STEP,
        "forward",
        "push",
        undefined,
        onboardingFadeTransition,
      );
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen style={onboardingContentStyle}>
        <div className="relative h-full w-full overflow-hidden bg-[#130c2d]">
          <OnboardingDarkBackground />

          <div
            className={cn(
              "relative z-10 flex h-full flex-col",
              safeAreaPadding({ top: "16px", bottom: "12px" }),
            )}
          >
            <OnboardingTopNav onBack={handleBack} progressFilled={2} />

            <div className="flex flex-col px-9 pt-9">
              <h1 className="font-[family-name:var(--font-crimson-text)] text-[35px] leading-[45px] font-semibold tracking-[-1px] text-white">
                A quick sustainability check.
              </h1>

              <div className="flex flex-col gap-4 pt-12">
                {OPTIONS.map((option) => (
                  <OnboardingOptionPill
                    key={option.key}
                    label={option.label}
                    sublabel={option.sublabel}
                    selected={selected === option.key}
                    onClick={() => handleSelect(option)}
                  />
                ))}
              </div>
            </div>

            <div className="mt-auto px-9 pt-6">
              <OnboardingPillButton
                text="Continue"
                onClick={handleContinue}
                disabled={selected !== "none" || isUpdating || isMedicalLoading}
              />
            </div>
          </div>
        </div>

        <OnboardingWarningAlert
          isOpen={warning !== null}
          message={warning ?? ""}
          onClose={() => setWarning(null)}
        />
      </IonContent>
    </IonPage>
  );
}
